import React, { useState, useMemo } from 'react';
import { Command } from 'cmdk';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Target, Inbox, ListTodo, Users, Search,
  Plus, Zap, Play, Focus, RefreshCw,
} from 'lucide-react';
import { T, springs } from '../../lib/tokens';

export const CommandPalette = ({ open, onClose, goals, tasks, agents, onNavigate, onAction }) => {
  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.25)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          zIndex: 999,
        }}
      />

      {/* Palette */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: -20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -20 }}
        transition={springs.snappy}
        style={{
          position: 'fixed',
          top: '20%', left: '50%', transform: 'translateX(-50%)',
          width: 560, maxHeight: '60vh',
          background: 'rgba(255,255,255,0.96)',
          backdropFilter: 'blur(40px) saturate(200%)',
          WebkitBackdropFilter: 'blur(40px) saturate(200%)',
          border: '1px solid rgba(255,255,255,0.9)',
          borderRadius: 16,
          boxShadow: '0 24px 80px rgba(0,0,0,0.18), 0 8px 24px rgba(0,0,0,0.08)',
          zIndex: 1000,
          overflow: 'hidden',
        }}
      >
        <Command label="Command palette" loop>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
            <Command.Input
              placeholder="Search or type a command..."
              autoFocus
              style={{
                width: '100%', border: 'none', outline: 'none',
                fontSize: 16, fontWeight: 400, color: T.text,
                fontFamily: 'inherit', background: 'transparent',
                padding: '4px 0',
              }}
            />
          </div>

          <Command.List style={{ maxHeight: 400, overflowY: 'auto', padding: '8px' }}>
            <Command.Empty style={{ padding: '24px 16px', textAlign: 'center', color: T.textMuted, fontSize: 14 }}>
              No results found.
            </Command.Empty>

            {/* Navigation */}
            <Command.Group heading={<span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '0 8px' }}>Navigate</span>}>
              {[
                { id: 'today', icon: Home, label: 'Today' },
                { id: 'goals', icon: Target, label: 'Goals' },
                { id: 'inbox', icon: Inbox, label: 'Inbox' },
                { id: 'tasks', icon: ListTodo, label: 'Tasks' },
                { id: 'agents', icon: Users, label: 'Agents' },
                { id: 'research', icon: Search, label: 'Research' },
              ].map(({ id, icon: Icon, label }) => (
                <Command.Item
                  key={id}
                  value={`go to ${label}`}
                  onSelect={() => { onNavigate(id); onClose(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                    fontSize: 14, color: T.textMid, fontFamily: 'inherit',
                  }}
                >
                  <Icon size={15} color={T.textMuted} />
                  {label}
                </Command.Item>
              ))}
            </Command.Group>

            {/* Actions */}
            <Command.Group heading={<span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '0 8px' }}>Actions</span>}>
              <Command.Item value="new task create task" onSelect={() => { onAction('quickCapture'); onClose(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: T.textMid, fontFamily: 'inherit' }}>
                <Plus size={15} color={T.textMuted} />
                New Task
                <kbd style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 4, background: 'rgba(0,0,0,0.06)', color: T.textMid }}>⌘N</kbd>
              </Command.Item>
              <Command.Item value="focus mode pomodoro" onSelect={() => { onAction('focusMode'); onClose(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: T.textMid, fontFamily: 'inherit' }}>
                <Zap size={15} color={T.textMuted} />
                Enter Focus Mode
                <kbd style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 600, padding: '1px 5px', borderRadius: 4, background: 'rgba(0,0,0,0.06)', color: T.textMid }}>F</kbd>
              </Command.Item>
              <Command.Item value="refresh data reload" onSelect={() => { onAction('refresh'); onClose(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: T.textMid, fontFamily: 'inherit' }}>
                <RefreshCw size={15} color={T.textMuted} />
                Refresh Data
              </Command.Item>
            </Command.Group>

            {/* Goals */}
            {goals?.length > 0 && (
              <Command.Group heading={<span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '0 8px' }}>Goals</span>}>
                {goals.slice(0, 5).map(g => (
                  <Command.Item key={g.id} value={`goal ${g.title}`} onSelect={() => { onNavigate('goals'); onClose(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: T.textMid, fontFamily: 'inherit' }}>
                    <Target size={15} color={T.textMuted} />
                    {g.title}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Agents */}
            {agents?.length > 0 && (
              <Command.Group heading={<span style={{ fontSize: 10, fontWeight: 600, color: T.textLight, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '0 8px' }}>Agents</span>}>
                {agents.map(a => (
                  <Command.Item key={a.id} value={`agent ${a.name} instruct`} onSelect={() => { onAction('instructAgent', a); onClose(); }} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 14, color: T.textMid, fontFamily: 'inherit' }}>
                    <Users size={15} color={T.textMuted} />
                    Instruct {a.name}
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>
        </Command>
      </motion.div>
    </>
  );
};
